/**
 * API client for the Privisy backend.
 */

import {
  ScanResult,
  ReportRecord,
  ReportSummary,
  DomainInfo,
  SalesReportSummary,
  ShareLinkRecord,
  PolicyAnalysis,
  PolicyAnalysisAdminSummary,
  PolicyAnalysisAdminRecord,
  AdminUserSummary,
  AdminUserDetails,
  AdminCreditGrantRequest,
  AdminCreditGrantResponse,
  BillingSubscriptionResponse,
  BillingSessionResponse,
  MonitorSchedule,
  MonitorListResponse,
  UnlockReportResponse,
  EmailReportRequest,
  EmailReportResponse,
  ShieldInterestEvent,
  ShieldInterestRequest,
  ShieldInterestResponse,
  PaywallOffer,
  PaywallEventRequest,
  QueueStatus,
  PurchaseStatusResponse,
  CreditInfoResponse,
  CreditHistoryResponse,
} from './types';
import { ApiError, ClaimError, ApiErrorBody } from './api-error';

// Declared in @privisy/api-types; re-exported so callers keep importing them from here.
export type {
  QueuedScanEntry,
  ActiveScanEntry,
  QueueStatus,
  PurchaseStatusResponse,
  CreditInfoResponse,
  CreditHistoryResponse,
} from './types';
import {
  mergeProgressFrame,
  watchScan,
  submitLiteScan,
  submitAnonymousAudit,
  QUEUE_BUSY_MESSAGE,
  hasConsent,
  type ScanProgress as SharedScanProgress,
  type ClickId,
  type LiteScanRejection,
} from '@privisy/ui-bridge';

export { ApiError, ClaimError } from './api-error';

type ApiErrorCtor = new (m: string, s: number, c: string, b: ApiErrorBody) => ApiError;
interface HandleOptions {
  /** Message that WINS over the body for a specific status — only for the four
   *  sites that already had a friendlier string than the API's own. */
  statusMessages?: Record<number, string>;
  ErrorClass?: ApiErrorCtor;
}

/** Throw a typed ApiError for a non-ok response, keeping the server's body. */
export async function assertOk(
  response: Response,
  fallbackMessage: string,
  options?: HandleOptions,
): Promise<void> {
  if (response.ok) return;
  const parsed = await response.json().catch(() => ({}));
  const body: ApiErrorBody =
    parsed && typeof parsed === 'object' ? (parsed as ApiErrorBody) : {};
  const code = typeof body.error === 'string' ? body.error : '';
  const bodyMessage = typeof body.message === 'string' ? body.message : '';
  const statusMessage = options?.statusMessages?.[response.status];
  const message = statusMessage || code || bodyMessage || fallbackMessage;
  const ErrorClass: ApiErrorCtor = options?.ErrorClass ?? ApiError;
  throw new ErrorClass(message, response.status, code, body);
}

/** assertOk + response.json(). Use assertOk directly for void endpoints. */
export async function handleResponse<T>(
  response: Response,
  fallbackMessage: string,
  options?: HandleOptions,
): Promise<T> {
  await assertOk(response, fallbackMessage, options);
  return response.json() as Promise<T>;
}

export const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4702/api';

// ---------------------------------------------------------------------------
// Centralized auth token injection
// ---------------------------------------------------------------------------

let _getToken: (() => Promise<string | null>) | null = null;

/**
 * Register a Clerk getToken callback so every API call automatically
 * includes an Authorization header when the user is signed in.
 */
export function setAuthProvider(fn: () => Promise<string | null>) {
  _getToken = fn;
}

/**
 * Wrapper around fetch() that injects a Bearer token when available.
 * Falls back to plain fetch when no auth provider is registered.
 */
async function authFetch(
  url: string,
  init?: RequestInit,
  getTokenCompat?: () => Promise<string | null>
): Promise<Response> {
  const token = _getToken ? await _getToken() : getTokenCompat ? await getTokenCompat() : null;
  const headers = new Headers(init?.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(url, { ...init, headers });
}

// ---------------------------------------------------------------------------
// Scan API
// ---------------------------------------------------------------------------

/**
 * The dashboard's view of a progress frame: the shared shape, with `result`
 * narrowed to this app's ScanResult. The frame type, the monotonic clamp and
 * the scan client itself all live in @privisy/ui-bridge (issue #740) — they
 * are re-exported here so every existing importer keeps its import path.
 */
export interface ScanProgress extends SharedScanProgress {
  result?: ScanResult;
}

export { mergeProgressFrame };

export interface ScanSectionOptions {
  uiCompliance: boolean;
  trackerDetection: boolean;
  gpcValidation: boolean;
  privacyPolicy: boolean;
}

export async function startScan(
  url: string,
  options?: { sections?: ScanSectionOptions; headless?: boolean; maxPages?: number },
  onProgress?: (progress: ScanProgress) => void,
  getToken?: () => Promise<string | null>
): Promise<ScanResult> {
  const response = await authFetch(`${API_BASE}/scans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url,
      sections: options?.sections,
      headless: options?.headless ?? true,
      ...(options?.maxPages !== undefined ? { maxPages: options.maxPages } : {}),
    }),
  }, getToken);

  // Same friendly copy the anonymous lite-scan lane already had — the one
  // shared 429 mapping from `@privisy/ui-bridge` (issue #932).
  await assertOk(response, 'Failed to start scan', { statusMessages: { 429: QUEUE_BUSY_MESSAGE } });

  const { scanId, streamUrl } = await response.json();

  // Connect to SSE stream if progress callback provided
  if (onProgress && streamUrl) {
    return awaitScan(scanIdFromStreamUrl(streamUrl) ?? scanId, onProgress, getToken);
  }

  // Fallback: poll for completion if no progress callback
  throw new Error('Progress callback required for scan');
}

/** `/api/scans/stream?scanId=<id>` → `<id>`. */
function scanIdFromStreamUrl(streamUrl: string): string | null {
  const match = /[?&]scanId=([^&]+)/.exec(streamUrl);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Follow a started scan to its terminal state over the shared client, which
 * carries the SSE stream, the status-poll fallback, the monotonic clamp and
 * the one timeout policy (issue #740 — this used to be a bespoke SSE handler
 * here with a 5s grace timer and no fallback at all).
 */
function awaitScan(
  scanId: string,
  onProgress: (progress: ScanProgress) => void,
  getToken?: () => Promise<string | null>,
): Promise<ScanResult> {
  return new Promise((resolve, reject) => {
    watchScan<ScanResult>({
      apiBase: API_BASE,
      scanId,
      onProgress: (progress) => onProgress(progress as ScanProgress),
      onComplete: (terminal) => {
        if (terminal.outcome === 'delivered') {
          resolve(terminal.result);
          return;
        }
        // The status-poll lane withholds the payload for unlocked scans, so a
        // completion seen there carries no result — read it back over the
        // authenticated endpoint the dashboard is entitled to.
        getScan(terminal.scanId, getToken).then(resolve, reject);
      },
      // Friendly copy, not the backend's raw string: this rejection is
      // rendered verbatim to the user.
      onFailure: (failure) => reject(new Error(failure.message)),
    });
  });
}

// ---------------------------------------------------------------------------
// Claim scan
// ---------------------------------------------------------------------------

export async function claimScan(scanId: string, intentToken: string): Promise<{ message: string; scanId: string }> {
  const response = await authFetch(`${API_BASE}/claim-scan`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scanId, intentToken }),
  });
  return handleResponse<{ message: string; scanId: string }>(response, 'Unknown error', { ErrorClass: ClaimError });
}

// ---------------------------------------------------------------------------
// Email me this report (#1780)
// ---------------------------------------------------------------------------

/**
 * Ask for a locked report's link by email, signed out. The intent token is
 * the write credential; `consent` is the ticked consent box. `meta` carries
 * the Meta browser ids for the server's Contact conversion; pass
 * readMetaAttribution(), which is null for an opted-out visitor. An
 * opted-out visitor also sends `optOut`, which stops the Contact itself. Throws
 * ApiError, whose `apiCode` the form maps to its copy.
 */
export async function emailReport(
  scanId: string,
  intentToken: string,
  email: string,
  meta?: { fbp?: string; fbc?: string } | null,
): Promise<EmailReportResponse> {
  const body: EmailReportRequest = { intentToken, email, consent: true };
  if (meta && (meta.fbp || meta.fbc)) body.meta = meta;
  // The API is cross-origin, so the server can't read the banner cookie.
  if (!hasConsent()) body.optOut = true;
  const response = await authFetch(`${API_BASE}/scans/${encodeURIComponent(scanId)}/email-report`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return handleResponse<EmailReportResponse>(response, 'Could not email the report');
}

// ---------------------------------------------------------------------------
// Anonymous lite scan
// ---------------------------------------------------------------------------

/**
 * The ApiError for a rejected anonymous submission. The typed `code` (#1695)
 * goes into the body so `apiCode` is set, and `retryAfter` rides along (#1992).
 */
function liteRejectionError(rejection: LiteScanRejection): ApiError {
  return new ApiError(rejection.message, rejection.status, rejection.code, {
    error: rejection.code,
    message: rejection.message,
    ...(rejection.apiCode !== undefined ? { code: rejection.apiCode } : {}),
    ...(rejection.retryAfter !== undefined ? { retryAfter: rejection.retryAfter } : {}),
  });
}

/**
 * Start an anonymous lite scan (no auth). Mirrors the marketing site's
 * LiteScanWidget entry point: the returned intentToken is the claim secret the
 * signed-out visitor uses to attach the scan to an account after sign-up.
 *
 * Delegates to `@privisy/ui-bridge`'s `submitLiteScan` — the one lite-scan
 * submission client shared with the marketing widget (issue #779 slice 1).
 * This wrapper keeps its own throw-based signature so existing callers
 * (`AnonymousScanForm`) and their tests are unaffected.
 *
 * `adsLead` carries the Google Ads click id and the lead event id the client
 * already sent as gtag's transaction_id, so the backend can re-upload the
 * conversion server-side if the browser's own hit never made it out. Omitted
 * for a visitor who has opted out or is signalling GPC.
 */
export async function startLiteScan(
  url: string,
  mode?: 'gpc',
  adsLead?: { leadEventId: string; clickId: ClickId } | null,
): Promise<{ scanId: string; intentToken: string }> {
  const outcome = await submitLiteScan({
    apiBase: API_BASE,
    url,
    mode,
    leadEventId: adsLead?.leadEventId,
    clickId: adsLead?.clickId,
  });
  if (outcome.accepted) {
    return { scanId: outcome.scanId, intentToken: outcome.intentToken };
  }
  throw liteRejectionError(outcome.rejection);
}

/** What `startAnonymousAudit` resolves to once the server accepts the audit. */
export interface AnonymousAuditStart {
  scanId: string;
  intentToken: string;
  /** Set when the server ran the GPC-only lite scan past its daily ceiling (#1778). */
  fallback?: 'gpc-only';
}

/**
 * Start an anonymous full audit (no auth, #1777): the full four-section audit,
 * persisted locked and ownerless, claimed later with the returned intentToken
 * exactly like a lite scan. Throws `ApiError` on rejection, as
 * `startLiteScan` does. No caller until the #1782 cutover.
 */
export async function startAnonymousAudit(
  url: string,
  adsLead?: { leadEventId: string; clickId: ClickId } | null,
): Promise<AnonymousAuditStart> {
  const outcome = await submitAnonymousAudit({
    apiBase: API_BASE,
    url,
    leadEventId: adsLead?.leadEventId,
    clickId: adsLead?.clickId,
  });
  if (outcome.accepted) {
    // Past its daily ceiling the server ran the GPC-only lite scan (#1778).
    return {
      scanId: outcome.scanId,
      intentToken: outcome.intentToken,
      ...(outcome.fallback ? { fallback: outcome.fallback } : {}),
    };
  }
  throw liteRejectionError(outcome.rejection);
}

// Note: getToken params are accepted for caller compatibility as fallback —
// authFetch prefers setAuthProvider(), then falls back to per-call getToken.

export async function getScans(getTokenCompat?: () => Promise<string | null>): Promise<{ scans: ScanResult[]; total: number }> {
  const response = await authFetch(`${API_BASE}/scans`, undefined, getTokenCompat);
  return handleResponse(response, 'Failed to fetch scans');
}

export async function getScan(id: string, getTokenCompat?: () => Promise<string | null>): Promise<ScanResult> {
  const response = await authFetch(`${API_BASE}/scans/${encodeURIComponent(id)}`, undefined, getTokenCompat);
  return handleResponse(response, 'Failed to fetch scan');
}

/*
 * The public GET /api/scan-status/:id lane lives in @privisy/ui-bridge now:
 * `watchScan` is its only caller, and its `ScanStatusPayload` is the one
 * response shape (issue #740). A second wrapper here was a fourth path to the
 * same endpoint with no production caller left.
 */

export async function healthCheck(): Promise<{ status: string }> {
  const response = await fetch(`${API_BASE}/health`);
  return handleResponse(response, 'API is not available');
}

// ---------------------------------------------------------------------------
// Report API
// ---------------------------------------------------------------------------

export async function createReport(
  scanId: string,
  notes?: string,
  getTokenCompat?: () => Promise<string | null>
): Promise<ReportRecord> {
  const response = await authFetch(`${API_BASE}/reports`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scanId, notes }),
  }, getTokenCompat);
  return handleResponse(response, 'Failed to create report');
}

export type ReportSortField = 'date' | 'score' | 'domain';
export type ReportSortOrder = 'asc' | 'desc';

export interface GetReportsOptions {
  domain?: string;
  /** Case-insensitive substring over domain and url. */
  search?: string;
  sort?: ReportSortField;
  order?: ReportSortOrder;
  /** Collapse repeat scans to the newest report per domain. */
  latestPerDomain?: boolean;
  limit?: number;
  offset?: number;
  /** Admin only — scope the list to another user. */
  userId?: string;
}

export async function getReports(
  options?: GetReportsOptions,
  getTokenCompat?: () => Promise<string | null>
): Promise<{ reports: ReportSummary[]; total: number; limit: number; offset: number }> {
  const params = new URLSearchParams();
  if (options?.domain) params.set('domain', options.domain);
  if (options?.search) params.set('search', options.search);
  if (options?.sort) params.set('sort', options.sort);
  if (options?.order) params.set('order', options.order);
  if (options?.latestPerDomain) params.set('latestPerDomain', 'true');
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  if (options?.userId) params.set('userId', options.userId);

  const qs = params.toString();
  const response = await authFetch(`${API_BASE}/reports${qs ? `?${qs}` : ''}`, undefined, getTokenCompat);
  return handleResponse(response, 'Failed to fetch reports');
}

export async function getReport(id: string, getTokenCompat?: () => Promise<string | null>): Promise<ReportRecord> {
  const response = await authFetch(`${API_BASE}/reports/${encodeURIComponent(id)}`, undefined, getTokenCompat);
  return handleResponse(response, 'Failed to fetch report');
}

export async function deleteReport(id: string, getTokenCompat?: () => Promise<string | null>): Promise<void> {
  const response = await authFetch(`${API_BASE}/reports/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  }, getTokenCompat);
  await assertOk(response, 'Failed to delete report');
}

/**
 * Spend one credit to unlock a locked full-audit report in place (#1760).
 * On failure the ApiError's body carries the typed `code`:
 * INSUFFICIENT_CREDITS (402, with buyUrl), REPORT_NOT_UNLOCKABLE (409) or
 * REPORT_NOT_FOUND (404).
 */
export async function unlockReport(
  id: string,
  getTokenCompat?: () => Promise<string | null>,
): Promise<UnlockReportResponse> {
  const response = await authFetch(`${API_BASE}/reports/${encodeURIComponent(id)}/unlock`, {
    method: 'POST',
  }, getTokenCompat);
  return handleResponse(response, 'Failed to unlock report');
}

export async function getReportDomains(getTokenCompat?: () => Promise<string | null>): Promise<{ domains: DomainInfo[] }> {
  const response = await authFetch(`${API_BASE}/reports/domains`, undefined, getTokenCompat);
  return handleResponse(response, 'Failed to fetch report domains');
}

export async function getReportSummary(reportId: string, shareToken?: string | null): Promise<SalesReportSummary> {
  const url = new URL(`${API_BASE}/reports/${encodeURIComponent(reportId)}/summary`);
  if (shareToken) {
    url.searchParams.set('token', shareToken);
  }
  // Use authFetch so authenticated owners can load their own summary without a share token
  const response = await authFetch(url.toString());
  return handleResponse(response, 'Failed to fetch report summary');
}

/**
 * Record a report reader's step through the Privisy Shield offer, a fake-door
 * test (Shield is not built). `email` is required for `reserved` and ignored
 * otherwise. Throws ApiError, whose `apiCode` the offer maps to its copy.
 */
export async function recordShieldInterest(
  reportId: string,
  event: ShieldInterestEvent,
  email?: string,
): Promise<ShieldInterestResponse> {
  const body: ShieldInterestRequest = event === 'reserved' ? { event, email } : { event };
  const response = await authFetch(`${API_BASE}/reports/${encodeURIComponent(reportId)}/shield-interest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return handleResponse<ShieldInterestResponse>(response, 'Could not record your interest');
}

/**
 * Record that the reader pressed a paywall offer on this report (migration
 * 044), so a price test can count readers who reached the price. Fire and
 * forget: `keepalive` lets it finish while the page leaves for Stripe, and a
 * failure never stands between the reader and the checkout.
 */
export function recordPaywallClick(reportId: string, offer: PaywallOffer): void {
  const body: PaywallEventRequest = { event: 'cta_clicked', offer };
  authFetch(`${API_BASE}/reports/${encodeURIComponent(reportId)}/paywall-event`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => undefined);
}

/**
 * URL of one proof-of-scan screenshot (epic #1331). A plain string so a page
 * that already holds a share token can put it straight on an <img>; the
 * dashboard's own pages go through fetchReportScreenshot instead, because an
 * <img> cannot carry the Clerk Bearer token authFetch attaches.
 */
export function reportScreenshotUrl(reportId: string, key: string, shareToken?: string | null): string {
  const url = new URL(`${API_BASE}/reports/${encodeURIComponent(reportId)}/screenshots/${encodeURIComponent(key)}`);
  if (shareToken) {
    url.searchParams.set('token', shareToken);
  }
  return url.toString();
}

/** Fetch one proof-of-scan screenshot as a Blob (for URL.createObjectURL). */
export async function fetchReportScreenshot(reportId: string, key: string, shareToken?: string | null): Promise<Blob> {
  const response = await authFetch(reportScreenshotUrl(reportId, key, shareToken));
  if (!response.ok) {
    throw new Error(`Failed to fetch screenshot ${key} (${response.status})`);
  }
  return response.blob();
}

export async function getAdminPolicyAnalyses(
  options?: {
    domain?: string;
    status?: string;
    requestSource?: 'url' | 'text';
    since?: string;
    limit?: number;
    offset?: number;
  },
  getTokenCompat?: () => Promise<string | null>
): Promise<{ analyses: PolicyAnalysisAdminSummary[]; total: number; limit: number; offset: number }> {
  const params = new URLSearchParams();
  if (options?.domain) params.set('domain', options.domain);
  if (options?.status) params.set('status', options.status);
  if (options?.requestSource) params.set('requestSource', options.requestSource);
  if (options?.since) params.set('since', options.since);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));

  const qs = params.toString();
  const response = await authFetch(`${API_BASE}/admin/policy-analyzer${qs ? `?${qs}` : ''}`, undefined, getTokenCompat);
  return handleResponse(response, 'Failed to fetch policy analysis history');
}

export async function getAdminPolicyAnalysisById(
  id: string,
  getTokenCompat?: () => Promise<string | null>
): Promise<PolicyAnalysisAdminRecord> {
  const response = await authFetch(`${API_BASE}/admin/policy-analyzer/${encodeURIComponent(id)}`, undefined, getTokenCompat);
  return handleResponse(response, 'Failed to fetch policy analysis details', { statusMessages: { 404: 'Policy analysis not found' } });
}

export async function getAdminUsers(
  getTokenCompat?: () => Promise<string | null>
): Promise<{ users: AdminUserSummary[]; total: number }> {
  const response = await authFetch(`${API_BASE}/admin/users`, undefined, getTokenCompat);
  return handleResponse(response, 'Failed to fetch admin users');
}

export async function getAdminUserDetails(
  id: string,
  getTokenCompat?: () => Promise<string | null>
): Promise<AdminUserDetails> {
  const response = await authFetch(`${API_BASE}/admin/users/${encodeURIComponent(id)}`, undefined, getTokenCompat);
  return handleResponse(response, 'Failed to fetch admin user details', { statusMessages: { 404: 'User not found' } });
}

/** Grant free credits to a user (an admin comp). Resolves to the new balance. */
export async function grantAdminCredits(
  id: string,
  amount: number,
  reason: string,
  getTokenCompat?: () => Promise<string | null>
): Promise<AdminCreditGrantResponse> {
  const body: AdminCreditGrantRequest = { amount, reason };
  const response = await authFetch(`${API_BASE}/admin/users/${encodeURIComponent(id)}/credits`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }, getTokenCompat);
  return handleResponse(response, 'Failed to grant credits');
}

// ---------------------------------------------------------------------------
// Queue management API
// ---------------------------------------------------------------------------




export async function getQueueStatus(): Promise<QueueStatus> {
  const response = await authFetch(`${API_BASE}/queue`);
  return handleResponse(response, 'Failed to fetch queue status');
}

export async function cancelQueuedScan(scanId: string): Promise<void> {
  const response = await authFetch(`${API_BASE}/queue/${encodeURIComponent(scanId)}`, {
    method: 'DELETE',
  });
  await assertOk(response, 'Failed to cancel scan');
}

export async function reorderQueuedScan(
  scanId: string,
  direction: 'up' | 'down'
): Promise<{ scanId: string; position: number }> {
  const response = await authFetch(`${API_BASE}/queue/${encodeURIComponent(scanId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ direction }),
  });
  return handleResponse(response, 'Failed to reorder scan');
}

// ---------------------------------------------------------------------------
// Share links API
// ---------------------------------------------------------------------------

export type ShareExpiry = '30m' | '1h' | '6h' | '24h' | '7d' | '30d';

export async function createShareLink(params: {
  resourceId: string;
  userId: string;
  expiresIn?: ShareExpiry;
  label?: string;
}): Promise<ShareLinkRecord> {
  const response = await authFetch(`${API_BASE}/shares`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  return handleResponse(response, 'Failed to create share link');
}

export async function getShareLinks(reportId: string): Promise<ShareLinkRecord[]> {
  const response = await authFetch(`${API_BASE}/shares/report/${encodeURIComponent(reportId)}`);
  const data = await handleResponse<{ links?: ShareLinkRecord[] } & ShareLinkRecord[]>(response, 'Failed to fetch share links');
  return data.links ?? data;
}

export async function revokeShareLink(id: string): Promise<void> {
  const response = await authFetch(`${API_BASE}/shares/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  await assertOk(response, 'Failed to revoke share link');
}

// ---------------------------------------------------------------------------
// Payment / Purchase API
// ---------------------------------------------------------------------------


/** @deprecated Use getCreditPurchaseStatus — kept for backward compat with old scan-based purchases */
export async function getPurchaseStatus(sessionId: string): Promise<PurchaseStatusResponse> {
  const response = await fetch(`${API_BASE}/purchase-status?session_id=${encodeURIComponent(sessionId)}`);
  return handleResponse(response, 'Failed to fetch purchase status');
}

/** Credit-based purchase status — returns { status, creditsAdded } */
export async function getCreditPurchaseStatus(sessionId: string): Promise<PurchaseStatusResponse> {
  return getPurchaseStatus(sessionId);
}

export async function verifyPurchase(sessionId: string): Promise<PurchaseStatusResponse> {
  const response = await authFetch(`${API_BASE}/verify-purchase?session_id=${encodeURIComponent(sessionId)}`);
  if (response.status === 402) return { status: 'pending' };
  return handleResponse(response, 'Failed to verify purchase');
}

// ---------------------------------------------------------------------------
// Credits API
// ---------------------------------------------------------------------------


export async function getCreditInfo(): Promise<CreditInfoResponse> {
  const response = await authFetch(`${API_BASE}/credits`);
  return handleResponse(response, 'Failed to fetch credit info');
}


export async function getCreditHistory(limit = 50, offset = 0): Promise<CreditHistoryResponse> {
  const response = await authFetch(`${API_BASE}/credits/history?limit=${limit}&offset=${offset}`);
  return handleResponse(response, 'Failed to fetch credit history');
}

// ---------------------------------------------------------------------------
// Billing API — recurring plans (#1315)
// ---------------------------------------------------------------------------

export type BillingInterval = 'annual' | 'monthly';

/**
 * Start a Stripe Checkout Session for a recurring plan; resolves to the Checkout url.
 * `reportId` names a report the webhook unlocks once the subscription starts (#1775).
 */
export async function createBillingCheckoutSession(
  plan: 'monitor',
  interval: BillingInterval = 'annual',
  reportId?: string,
  meta?: { fbp?: string; fbc?: string } | null,
): Promise<BillingSessionResponse> {
  const payload: Record<string, unknown> = { plan, interval };
  if (reportId) payload.reportId = reportId;
  if (meta && (meta.fbp || meta.fbc)) payload.meta = meta;
  const response = await authFetch(`${API_BASE}/billing/checkout-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse(response, 'Failed to start checkout');
}

/**
 * Start a one-off $49 Checkout Session that unlocks this report (#1775); the
 * webhook unlocks it, and Stripe returns to `#/scan/:id?session_id=…`.
 * Signed out, `intentToken` (the report's claim credential) stands in for the
 * account, and the webhook gives the purchase to the checkout email's account.
 * 401 = sign in first (the report is already claimed), 403 = not the owner,
 * 404 = no such report or a wrong token, 409 = already unlocked or not
 * unlockable, 503 = billing not configured.
 */
export async function createUnlockCheckout(
  reportId: string,
  meta?: { fbp?: string; fbc?: string } | null,
  intentToken?: string | null,
): Promise<BillingSessionResponse> {
  const payload: Record<string, unknown> = { reportId };
  if (meta && (meta.fbp || meta.fbc)) payload.meta = meta;
  if (intentToken) payload.intentToken = intentToken;
  const response = await authFetch(`${API_BASE}/billing/unlock-checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse(response, 'Failed to start checkout');
}

/** Open a Stripe Billing Portal session for the signed-in customer. 404 = no Stripe customer. */
export async function createBillingPortalSession(): Promise<BillingSessionResponse> {
  const response = await authFetch(`${API_BASE}/billing/portal-session`, { method: 'POST' });
  return handleResponse(response, 'Failed to open billing portal');
}

/** The caller's current subscription; `status: 'none'` when there is none. */
export async function getSubscription(): Promise<BillingSubscriptionResponse> {
  const response = await authFetch(`${API_BASE}/billing/subscription`);
  return handleResponse(response, 'Failed to fetch subscription');
}

// ---------------------------------------------------------------------------
// Continuous monitoring (#1318)
// ---------------------------------------------------------------------------

/** The caller's monitored domains and their tier's domain limit. */
export async function listMonitors(): Promise<MonitorListResponse> {
  const response = await authFetch(`${API_BASE}/monitors`);
  return handleResponse(response, 'Failed to fetch monitored domains');
}

/** Monitor a domain weekly. 403 = tier or domain limit, 409 = already monitored. */
export async function createMonitor(startUrl: string): Promise<MonitorSchedule> {
  const response = await authFetch(`${API_BASE}/monitors`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ startUrl }),
  });
  return handleResponse(response, 'Failed to monitor domain');
}

/** Stop monitoring a domain. */
export async function deleteMonitor(id: string): Promise<void> {
  const response = await authFetch(`${API_BASE}/monitors/${encodeURIComponent(id)}`, { method: 'DELETE' });
  await assertOk(response, 'Failed to remove monitored domain');
}

// ---------------------------------------------------------------------------
// Conversion Tracking
// ---------------------------------------------------------------------------

export async function trackLinkedInSignup(email: string, userId: string): Promise<void> {
  await authFetch(`${API_BASE}/track/linkedin-signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, userId }),
  });
}

// ---------------------------------------------------------------------------
// Policy Analyzer
// ---------------------------------------------------------------------------

export async function getPolicyAnalysis(id: string): Promise<PolicyAnalysis> {
  const response = await fetch(`${API_BASE}/policy-analyzer/${encodeURIComponent(id)}`);
  return handleResponse(response, 'Failed to fetch policy analysis', { statusMessages: { 404: 'Policy analysis not found or expired' } });
}
